import i18nFactory from '@yandex-int/i18n';
import { uniqBy } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { from, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { Action } from '../../actions';
import {
    changeLocationHard,
    dummyAction,
    updateRegions
} from '../../actions/common';
import { NoticeType, showNoticeMessage } from '../../actions/grid-screen';
import {
    ADD_PHRASES_START_INPUT_ITEMS,
    addPhrasesStartInputItems,
    AddPhrasesStartInputItems,
    AddSearchedAlsoPhrasesToStartPhrases,
    closePhrasesSelectorWizard,
    FETCH_PHRASES_FOR_PHRASES_SELECTOR_WIZARD,
    FetchPhrasesForPhrasesSelectorWizardAction,
    OPEN_PHRASES_SELECTOR_WIZARD,
    OpenPhrasesSelectorWizard,
    PHRASES_SELECTOR_ADD_PHRASES_TO_MINUS_WORDS_LIST,
    PHRASES_SELECTOR_ADD_PHRASES_TO_START_LIST,
    PHRASES_SELECTOR_REMOVE_PHRASES_FROM_MINUS_WORDS_LIST,
    PHRASES_SELECTOR_SUBMIT_SELECTION_WIZARD,
    selectionChange,
    SelectionWizardListName,
    showPhrasesSelectorWizard,
    SubmitSelectionWizard,
    updatePhrasesSelectorWizardState
} from '../../actions/phrasesSelectorWizard';
import {
    GetKeywordStatShowResponse,
    RestApiManager
} from '../../api/RestApiManager';
import { phraseIdFieldName } from '../../declarations/filter/filter';
import { mapCampaignTypeToCalcType } from '../../mappers/filter/filter';
import { mapPhraseToPhrasesSelectorListItem } from '../../mappers/phrasesSelectorWizard';
import { awaitCommonData } from '../../operators';
import { selectPhrasesSelectorWizard } from '../../selectors/phrasesSelectorWizard/phrasesSelectorWizard';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { serializeFilterToString } from '../../service/FilterHelpers/FilterHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import '../../service/Rx';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { GRID_PREFIX, phrases } from '../../typings/common';
import { CampaignType } from '../../typings/domain';
import { FilterExpressionValueType, Operator } from '../../typings/filter';
import { GridScreenUrlSearchQueryParams } from '../../typings/grid';
import { State } from '../../typings/state';

import * as keyset from './phrasesSelectorWizard.i18n';

const i18n = i18nFactory(keyset);

async function keywordsStatRequest(
    phrasesList: string[],
    commonMinusPhrases: string[],
    regionsIds: number[],
    apiManager: RestApiManager
): Promise<GetKeywordStatShowResponse[]> {
    const result: GetKeywordStatShowResponse[] = [];

    for (const ph of phrasesList) {
        const keywordStat = await apiManager.getKeywordStatShow({
            phrase: ph,
            commonMinusPhrases,
            geo: regionsIds
        });

        result.push(keywordStat);
    }

    return result;
}

export const submitStartPhraseInputValueEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(
            ADD_PHRASES_START_INPUT_ITEMS,
            PHRASES_SELECTOR_REMOVE_PHRASES_FROM_MINUS_WORDS_LIST,
            PHRASES_SELECTOR_ADD_PHRASES_TO_MINUS_WORDS_LIST
        )
        .mergeMap((action: AddPhrasesStartInputItems) => {
            const state = store$.value;

            const phrasesSelectorWizard = selectPhrasesSelectorWizard(
                state,
                action.wizardId
            );

            const phrasesList = action.value
                ? action.value
                : phrasesSelectorWizard.startPhrases.map(el => el.text);

            const commonMinusPhrases = phrasesSelectorWizard.minusWordsList.map(
                el => el.text
            );

            const request = keywordsStatRequest(
                phrasesList,
                commonMinusPhrases,
                phrasesSelectorWizard.groupInfo.regionIds,
                deps.restApiManager
            );

            return Observable.fromPromise(request).mergeMap(
                (result: GetKeywordStatShowResponse[]) => {
                    const keywordsStat = phrasesList.reduce(
                        (acc, el, index) => {
                            acc[el] = {
                                searchedWith: result[index].searchedWith.map(
                                    mapPhraseToPhrasesSelectorListItem
                                ),
                                searchedAlso: result[index].searchedAlso.map(
                                    mapPhraseToPhrasesSelectorListItem
                                )
                            };

                            return acc;
                        },
                        {}
                    );

                    const newPhrases = phrasesList.filter(
                        el =>
                            !phrasesSelectorWizard.startPhrases.find(
                                item => item.text === el
                            )
                    );

                    const newStartPhrasesList = newPhrases.map(el =>
                        mapPhraseToPhrasesSelectorListItem({
                            shows: 0,
                            phrase: el
                        })
                    );

                    const startPhrases = uniqBy(
                        [
                            ...phrasesSelectorWizard.startPhrases,
                            ...newStartPhrasesList
                        ],
                        'text'
                    );

                    const actions = [
                        updatePhrasesSelectorWizardState(action.wizardId, {
                            keywordsStat,
                            startPhrases,
                            isInProgress: false
                        }),
                        selectionChange(
                            action.wizardId,
                            startPhrases.map(
                                el =>
                                    el.selected || newPhrases.includes(el.text)
                            ),
                            SelectionWizardListName.startPhrases
                        )
                    ];

                    return actions;
                }
            );
        });
};

export const submitSelectionWizardEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(PHRASES_SELECTOR_SUBMIT_SELECTION_WIZARD).mergeMap(
        (action: SubmitSelectionWizard): Observable<Action> => {
            const { commonData } = store$.value;
            const phrasesSelectorWizard = selectPhrasesSelectorWizard(
                store$.value,
                action.wizardId
            );
            const campaignType = phrasesSelectorWizard.groupInfo.campaign.type;
            const login = commonData.client.login;

            const adGroupId = phrasesSelectorWizard.groupInfo.id;
            const priceContext = phrasesSelectorWizard.priceContext;

            const addItems = phrasesSelectorWizard.chosenPhrases.map(el => ({
                keyword: el.text,
                adGroupId,
                priceContext: priceContext ? String(priceContext) : undefined
            }));

            const minusKeywords = phrasesSelectorWizard.minusWordsList.map(
                el => el.text
            );

            if (!login) {
                throw new Error('login is undefined');
            }

            const addKeywordsPromise = addItems.length
                ? deps.phrasesMutationExecutor
                      .addKeywords(
                          {
                              addItems
                          },
                          login
                      )
                      .then(response =>
                          response.addKeywords.addedItems.map(
                              el => el.keywordId
                          )
                      )
                : Promise.resolve([]);

            const addAddGroupMinusKeywordsPromise = minusKeywords.length
                ? deps.phrasesMutationExecutor
                      .addAddGroupMinusKeywords(
                          {
                              addItems: [
                                  {
                                      minusKeywords,
                                      adGroupId
                                  }
                              ]
                          },
                          login
                      )
                      .then(() => undefined)
                : Promise.resolve();

            return Observable.fromPromise(
                Promise.all([
                    addKeywordsPromise,
                    addAddGroupMinusKeywordsPromise
                ])
            )
                .mergeMap(
                    ([addKeywordsResponse]): Observable<Action> => {
                        const filter = serializeFilterToString([
                            {
                                field: phraseIdFieldName,
                                operator: Operator.EQUALS,
                                value: {
                                    type: FilterExpressionValueType.numberSet,
                                    value: addKeywordsResponse
                                }
                            }
                        ]);

                        const closeSelectionWizard$ = Observable.of(
                            closePhrasesSelectorWizard(action.wizardId)
                        ).merge(
                            Observable.of(
                                updatePhrasesSelectorWizardState(
                                    action.wizardId,
                                    {
                                        isInProgress: false,
                                        isVisible: false
                                    }
                                )
                            )
                        );

                        if (addKeywordsResponse.length) {
                            return closeSelectionWizard$.merge(
                                Observable.of(
                                    changeLocationHard(
                                        UrlHelpers.createNewDirectLink(
                                            UrlHelpers.createNewDirectUrlPathByParams(
                                                GRID_PREFIX,
                                                phrases
                                            ),
                                            {
                                                ulogin: login,
                                                filter,
                                                [GridScreenUrlSearchQueryParams.campaignsIds]: String(
                                                    phrasesSelectorWizard
                                                        .groupInfo.campaign.id
                                                ),
                                                [GridScreenUrlSearchQueryParams.groupsIds]: String(
                                                    phrasesSelectorWizard
                                                        .groupInfo.id
                                                ),
                                                [GridScreenUrlSearchQueryParams.calcType]: mapCampaignTypeToCalcType(
                                                    campaignType
                                                )
                                            }
                                        )
                                    )
                                )
                            );
                        }

                        return closeSelectionWizard$;
                    }
                )
                .catch((err: ApiMethodExecutingError) => {
                    if (!(err instanceof NotSyncedError)) {
                        showNoticeMessage(NoticeType.error, err.getMessage());
                    }

                    return Observable.of(
                        updatePhrasesSelectorWizardState(action.wizardId, {
                            isInProgress: false
                        })
                    );
                });
        }
    );
};

export const addPhraseToStartPhrasesEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = action$ => {
    return action$
        .ofType(PHRASES_SELECTOR_ADD_PHRASES_TO_START_LIST)
        .map((action: AddSearchedAlsoPhrasesToStartPhrases) => {
            const newPhrases = action.items.map(el => el.text);

            return addPhrasesStartInputItems(action.wizardId, newPhrases);
        });
};

export const openPhrasesSelectorWizardEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$) => {
    return action$.pipe(
        ofType(OPEN_PHRASES_SELECTOR_WIZARD),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: OpenPhrasesSelectorWizard): Observable<Action> => {
                const type = action.groupInfo.campaign.type;

                if (type === CampaignType.DYNAMIC) {
                    showNoticeMessage(
                        NoticeType.error,
                        i18n('no-phrases-for-dynamic-campaigns')
                    );

                    return Observable.of(dummyAction);
                }

                if (type === CampaignType.PERFORMANCE) {
                    showNoticeMessage(
                        NoticeType.error,
                        i18n('no-phrases-for-smart-banners')
                    );

                    return Observable.of(dummyAction);
                }

                return from(RegionsHelpers.fetchRegions()).pipe(
                    mergeMap(regionsResult => {
                        const showSelectionWizard: Array<Action> = [
                            showPhrasesSelectorWizard(action.wizardId)
                        ];

                        if (!store$.value.commonData.regions) {
                            showSelectionWizard.push(
                                updateRegions(regionsResult)
                            );
                        }

                        return showSelectionWizard;
                    })
                );
            }
        )
    );
};

export const fetchPhrasesForPhrasesSelectorWizardEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_PHRASES_FOR_PHRASES_SELECTOR_WIZARD),
        awaitCommonData(action$, store$),
        mergeMap(
            (
                action: FetchPhrasesForPhrasesSelectorWizardAction
            ): Observable<Action> => {
                return from(
                    deps.groupsDataProvider.getGroupKeywordsList(
                        store$.value.commonData.login,
                        action.groupInfo.campaign.id,
                        action.groupInfo.id
                    )
                ).pipe(
                    map(keywordsResult =>
                        addPhrasesStartInputItems(
                            action.wizardId,
                            keywordsResult
                        )
                    )
                );
            }
        )
    );
};
